import { getFirestore, setDoc, getDoc, doc, Timestamp, collection } from "firebase/firestore";
import { app } from "../config"
import { ref, uploadBytes, getDownloadURL, getStorage, deleteObject } from "firebase/storage";
import { isDemoMode } from "../../demo/isDemoMode";
import demoStore from "../../demo/demoStore";

const db = getFirestore(app);
const storage = getStorage();

// Helper to convert File to data URL for demo mode
const fileToDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Helper function to remove undefined values from an object (Firestore doesn't accept undefined)
const removeUndefinedValues = (obj) => {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item)).filter(item => item !== undefined);
  }

  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = removeUndefinedValues(value);
    }
  }
  return cleaned;
};

const hasAllRequiredFields = () => {
  return { success: true };
};

const createEventInFirestore = async (eventData, isDraft) => {
  try {
    // Check if eventData has a cover photo (make it optional - don't block event creation)
    if (eventData.eventCoverPhoto && eventData.eventCoverPhoto instanceof File) {
      if (isDemoMode) {
        // In demo mode, convert File to data URL
        try {
          const dataUrl = await fileToDataUrl(eventData.eventCoverPhoto);
          eventData.eventCoverPhoto = dataUrl;
        } catch (error) {
          console.warn("Could not convert cover photo to data URL:", error.message);
          delete eventData.eventCoverPhoto;
        }
      } else {
        // Production: upload to Firebase Storage
        try {
          // Delete the old cover photo from storage if replacing an existing event's image
          if (eventData.eventId) {
            try {
              const existingRef = doc(db, "events", eventData.eventId);
              const existingDoc = await getDoc(existingRef);
              const oldCoverPhoto = existingDoc.exists() ? existingDoc.data().eventCoverPhoto : null;
              if (oldCoverPhoto && typeof oldCoverPhoto === "string" && oldCoverPhoto.includes("firebasestorage.googleapis.com")) {
                const oldRef = ref(storage, oldCoverPhoto);
                await deleteObject(oldRef);
              }
            } catch (deleteError) {
              console.warn("Could not delete old cover photo:", deleteError.message);
            }
          }

          const timestamp = new Date().toISOString();
          const file = eventData.eventCoverPhoto;
          const fileName = `${file.name}_${timestamp}`;
          const storageRef = ref(storage, `events/${fileName}`);
          const uploadTaskSnapshot = await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);
          eventData.eventCoverPhoto = downloadURL;
        } catch (uploadError) {
          console.warn("Cover photo upload failed, continuing without it:", uploadError.message);
          delete eventData.eventCoverPhoto;
        }
      }
    } else if (eventData.eventCoverPhoto && typeof eventData.eventCoverPhoto !== 'string') {
      delete eventData.eventCoverPhoto;
    }

    // Check if eventData has eventDocuments (make uploads optional)
    if (eventData.eventDocuments && Array.isArray(eventData.eventDocuments)) {
      try {
        const updatedEventDocuments = await Promise.all(
          eventData.eventDocuments.map(async (docItem) => {
            if (docItem.file && docItem.fileName && docItem.file instanceof File) {
              if (isDemoMode) {
                // In demo mode, convert File to data URL
                try {
                  const dataUrl = await fileToDataUrl(docItem.file);
                  return { fileName: docItem.fileName, file: dataUrl };
                } catch (error) {
                  console.warn(`Document conversion failed for ${docItem.fileName}:`, error.message);
                  return { fileName: docItem.fileName, file: null };
                }
              } else {
                // Production: upload to Firebase Storage
                try {
                  const timestamp = new Date().toISOString();
                  const fileName = `${docItem.fileName}_${timestamp}`;
                  const storageRef = ref(storage, `documents/${fileName}`);
                  const uploadTaskSnapshot = await uploadBytes(storageRef, docItem.file);
                  const downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);
                  return { fileName: docItem.fileName, file: downloadURL };
                } catch (docUploadError) {
                  console.warn(`Document upload failed for ${docItem.fileName}:`, docUploadError.message);
                  return { fileName: docItem.fileName, file: null };
                }
              }
            }
            return docItem;
          })
        );
        eventData.eventDocuments = updatedEventDocuments;
      } catch (docsError) {
        console.warn("Event documents processing failed:", docsError.message);
      }
    }

    // Check if eventData has eventMap (make upload optional)
    if (eventData.eventMap && eventData.eventMap.file && eventData.eventMap.fileName) {
      if (eventData.eventMap.file instanceof File) {
        if (isDemoMode) {
          // In demo mode, convert File to data URL
          try {
            const dataUrl = await fileToDataUrl(eventData.eventMap.file);
            eventData.eventMap.file = dataUrl;
          } catch (error) {
            console.warn("Event map conversion failed:", error.message);
            eventData.eventMap = null;
          }
        } else {
          // Production: upload to Firebase Storage
          try {
            const timestamp = new Date().toISOString();
            const fileName = `${eventData.eventMap.fileName}_${timestamp}`;
            const storageRef = ref(storage, `maps/${fileName}`);
            const uploadTaskSnapshot = await uploadBytes(storageRef, eventData.eventMap.file);
            const downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);
            eventData.eventMap.file = downloadURL;
          } catch (mapUploadError) {
            console.warn("Event map upload failed:", mapUploadError.message);
            eventData.eventMap = null;
          }
        }
      }
    }

    // Prepare the event document
    const now = new Date();
    const eventDoc = {
      ...eventData,
      isDraft: isDraft,
      updatedAt: isDemoMode ? now : Timestamp.now(),
      createdAt: eventData.createdAt || (isDemoMode ? now : Timestamp.now()),
    };

    let eventId;

    if (isDemoMode) {
      // In demo mode, save to demoStore
      eventId = eventData.eventId || `event-${Date.now()}`;
      eventDoc.eventId = eventId;

      // Remove undefined values before saving
      const sanitizedEventDoc = removeUndefinedValues(eventDoc);

      demoStore.setDoc('events', eventId, sanitizedEventDoc);

      return { success: true, eventId };
    }

    // Production: save to Firestore
    let eventRef;

    if (eventData.eventId) {
      eventRef = doc(db, "events", eventData.eventId);
    } else {
      eventRef = doc(collection(db, "events"));
      eventDoc.eventId = eventRef.id;
    }

    // Remove undefined values before saving to Firestore
    const sanitizedEventDoc = removeUndefinedValues(eventDoc);

    // Set or update the event document in Firestore
    await setDoc(eventRef, sanitizedEventDoc, { merge: true });

    return { success: true, eventId: eventRef.id };
  } catch (error) {
    console.error("Error adding event document: ", error);
    return { success: false, error: error.message };
  }
};

const createEvent = async (request, isDraft = false) => {
  try {
    const eventData = { ...request };
    
    // Sanitize documents to ensure requiredFrom is always a valid array
    if (eventData.documents && Array.isArray(eventData.documents)) {
      eventData.documents = eventData.documents.map(doc => ({
        ...doc,
        documentName: doc.documentName || "",
        requiredFrom: Array.isArray(doc.requiredFrom) ? doc.requiredFrom.filter(Boolean) : [],
        isRequired: doc.isRequired !== undefined ? doc.isRequired : true
      }));
    }
    
    if (!isDraft) {
      const { success, message } = hasAllRequiredFields(eventData);
      if (!success) {
        throw new Error(message);
      }
    }

    console.log("About to create event in Firestore:", JSON.stringify(eventData, null, 2));
    const result = await createEventInFirestore(eventData, isDraft);
    console.log("Firestore result:", result);
    
    if (result.success) {
      return { success: true, message: "Event Created Successfully", eventId: result.eventId };
    } else {
      throw new Error(result.error || "Failed to create event in Firestore");
    }
  } catch (error) {
    console.error("Error processing the event creation request: ", error);
    return { success: false, message: error.message };
  }
};

export { createEvent };